import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthGuard } from "./components/AuthGuard";
import { DrawerNav } from "./components/DrawerNav";
import { useRole } from "./hooks/useRole";
import { setAuthToken } from "./config/custom-fetch";

import { ThemeProvider } from "./context/ThemeContext";
import { ToastProvider } from "./context/ToastContext";
import { ConversationViewsProvider } from "./context/ConversationViewsContext";

// Workspaces & Pages
import { ThreadsWorkspace } from "./pages/ThreadsWorkspace";
import { RunsWorkspace } from "./pages/RunsWorkspace";
import { RulesWorkspace } from "./pages/RulesWorkspace";
import { KnowledgeWorkspace } from "./pages/KnowledgeWorkspace";
import { GuardrailsWorkspace } from "./pages/GuardrailsWorkspace";
import { AgentConfigWorkspace } from "./pages/AgentConfigWorkspace";
import { BatchesWorkspace } from "./pages/BatchesWorkspace";
import { Menu, Database } from "lucide-react";
import { SettingsPage } from "./pages/SettingsPage";
import { UsersManagementPage } from "./pages/UsersManagementPage";
import { SnapshotsWorkspace } from "./pages/testing/SnapshotsWorkspace";
import { TestSuitesWorkspace } from "./pages/testing/TestSuitesWorkspace";
import { TestRunsWorkspace } from "./pages/testing/TestRunsWorkspace";
import { RunComparisonWorkspace } from "./pages/testing/RunComparisonWorkspace";

function AppLayout({ children }: { children: React.ReactNode }) {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth < 1280;
    }
    return false;
  });
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setIsCollapsed(true);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isStandardPage =
    location.pathname.startsWith("/settings") || location.pathname.startsWith("/users");

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-stone-50 dark:bg-stone-950 text-stone-900 dark:text-stone-100 selection:bg-indigo-200 dark:selection:bg-indigo-800 selection:text-stone-900 dark:selection:text-stone-100 transition-colors">
      <DrawerNav
        isCollapsed={isCollapsed}
        onToggleCollapsed={() => setIsCollapsed((prev) => !prev)}
        isMobileOpen={isMobileOpen}
        onCloseMobile={() => setIsMobileOpen(false)}
      />

      <div className="flex-1 flex flex-col h-screen min-w-0 overflow-hidden">
        {/* Mobile Top Navigation Bar (< md) */}
        <header className="md:hidden flex items-center justify-between px-3 h-12 bg-white dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800 shrink-0 select-none">
          <button
            type="button"
            onClick={() => setIsMobileOpen(true)}
            className="p-1.5 rounded-lg text-stone-600 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 transition"
            aria-label="Open navigation menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-stone-900 dark:bg-stone-800 text-white flex items-center justify-center">
              <Database className="w-3.5 h-3.5 text-emerald-400" />
            </div>
            <span className="font-serif font-bold text-sm text-stone-900 dark:text-stone-100">
              Silvestre
            </span>
          </div>
          <div className="w-8" />
        </header>

        <main
          className={
            isStandardPage
              ? "flex-1 min-w-0 overflow-y-scroll [scrollbar-gutter:stable] p-4 md:p-8"
              : "flex-1 min-w-0 overflow-hidden flex flex-col p-0"
          }
        >
          {children}
        </main>
      </div>
    </div>
  );
}

export function App() {
  const { accessToken, orgId } = useRole();

  useEffect(() => {
    setAuthToken(accessToken || null, orgId || null);
  }, [accessToken, orgId]);

  return (
    <ThemeProvider>
      <ToastProvider>
        <BrowserRouter>
          <AuthGuard>
            <ConversationViewsProvider>
              <AppLayout>
                <Routes>
                  {/* Default Route */}
                  <Route path="/" element={<Navigate to="/threads" replace />} />

                  {/* 3-Column Conversations Workspace */}
                  <Route path="/threads" element={<ThreadsWorkspace />} />
                  <Route path="/threads/:thread_id" element={<ThreadsWorkspace />} />

                  {/* 3-Column Feedback Batches Workspace */}
                  <Route path="/batches" element={<BatchesWorkspace />} />
                  <Route path="/batches/:batch_id" element={<BatchesWorkspace />} />

                  {/* 3-Column Execution Runs & Traces */}
                  <Route path="/runs" element={<RunsWorkspace />} />
                  <Route path="/runs/:run_id" element={<RunsWorkspace />} />

                  {/* Testing & A/B Prompt Experimentation Workspaces */}
                  <Route path="/snapshots" element={<SnapshotsWorkspace />} />
                  <Route path="/test-suites" element={<TestSuitesWorkspace />} />
                  <Route path="/test-suites/:suite_id" element={<TestSuitesWorkspace />} />
                  <Route path="/test-suites/:suite_id/cases/:case_id" element={<TestSuitesWorkspace />} />
                  <Route path="/test-runs" element={<TestRunsWorkspace />} />
                  <Route path="/test-runs/compare" element={<RunComparisonWorkspace />} />
                  <Route path="/test-runs/:run_id" element={<TestRunsWorkspace />} />

                  {/* 3-Column Operational Rules Directives */}
                  <Route path="/rules" element={<RulesWorkspace />} />
                  <Route path="/rules/new" element={<RulesWorkspace />} />
                  <Route path="/rules/export" element={<RulesWorkspace />} />
                  <Route path="/rules/:rule_id" element={<RulesWorkspace />} />

                  {/* 3-Column Authoritative Knowledge Items & Vectors */}
                  <Route path="/knowledge" element={<KnowledgeWorkspace />} />
                  <Route path="/knowledge/chunk/:chunk_id" element={<KnowledgeWorkspace />} />
                  <Route path="/knowledge/chunks/:chunk_id" element={<KnowledgeWorkspace />} />
                  <Route path="/knowledge/:item_id" element={<KnowledgeWorkspace />} />
                  <Route path="/items" element={<Navigate to="/knowledge" replace />} />
                  <Route path="/items/:item_id" element={<Navigate to="/knowledge" replace />} />

                  {/* 3-Column Safety Guardrails Manifold */}
                  <Route path="/guardrails" element={<GuardrailsWorkspace />} />
                  <Route path="/guardrails/:item_id" element={<GuardrailsWorkspace />} />

                  {/* 3-Column Agent Configurations Workspace */}
                  <Route path="/agents" element={<AgentConfigWorkspace />} />
                  <Route path="/agents/:agent_type" element={<AgentConfigWorkspace />} />

                  {/* Platform Users & Agents */}
                  <Route path="/users" element={<UsersManagementPage />} />

                  {/* Settings & Diagnostics */}
                  <Route path="/settings" element={<SettingsPage />} />

                  {/* Catch-all fallback */}
                  <Route path="*" element={<Navigate to="/threads" replace />} />
                </Routes>
              </AppLayout>
            </ConversationViewsProvider>
          </AuthGuard>
        </BrowserRouter>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
